/**
 * School alias / merge utilities for analytics deduplication.
 */

import { normalizeSchool } from "./aggregateAnalytics";

const ALIASES_DOC_PATH = ["analytics_config", "school_aliases"];

export { ALIASES_DOC_PATH };

/** @typedef {{ id: string, canonicalName: string, memberKeys: string[], memberLabels: string[] }} AliasGroup */

/**
 * Build lookup: normalized raw key → { groupKey, displayName }
 * @param {AliasGroup[]} groups
 */
export function buildAliasLookup(groups) {
  const lookup = new Map();
  for (const g of groups) {
    const groupKey = normalizeSchool(g.canonicalName) || g.id;
    const displayName = g.canonicalName;
    const keys = new Set([
      groupKey,
      ...(g.memberKeys || []).map((k) => normalizeSchool(k)),
    ]);
    for (const k of keys) {
      if (k) lookup.set(k, { groupKey, displayName });
    }
  }
  return lookup;
}

/**
 * Resolve a school name to its canonical group key.
 * @param {string|null|undefined} schoolName
 * @param {Map} lookup from buildAliasLookup
 */
export function resolveSchoolKey(schoolName, lookup) {
  const norm = normalizeSchool(schoolName) || "(unknown)";
  const hit = lookup.get(norm);
  return hit ? hit.groupKey : norm;
}

export function resolveSchoolDisplay(schoolName, lookup) {
  const norm = normalizeSchool(schoolName) || "(unknown)";
  const hit = lookup.get(norm);
  if (hit) return hit.displayName;
  return (schoolName ?? "").trim() || "(unknown)";
}

/**
 * Attach canonical school fields to lean records.
 */
export function applySchoolAliases(records, lookup) {
  return records.map((r) => ({
    ...r,
    schoolKey: resolveSchoolKey(r.schoolName, lookup),
    schoolDisplay: resolveSchoolDisplay(r.schoolName, lookup),
  }));
}

/** @typedef {{ key: string, label: string }} HiddenSchool */

/**
 * Normalized set of hidden school keys for fast lookup.
 * @param {HiddenSchool[]} hiddenSchools
 */
export function buildHiddenKeySet(hiddenSchools = []) {
  return new Set(
    hiddenSchools.map((s) => normalizeSchool(s.key)).filter(Boolean)
  );
}

/**
 * Exclude records whose canonical schoolKey is hidden.
 * @param {object[]} records
 * @param {HiddenSchool[] | Set<string>} hiddenSchoolsOrKeys
 */
export function filterHiddenRecords(records, hiddenSchoolsOrKeys) {
  const hidden =
    hiddenSchoolsOrKeys instanceof Set
      ? hiddenSchoolsOrKeys
      : buildHiddenKeySet(hiddenSchoolsOrKeys);
  if (hidden.size === 0) return records;
  return records.filter((r) => {
    const key =
      (r.schoolKey ?? normalizeSchool(r.schoolName)) || "(unknown)";
    return !hidden.has(key);
  });
}

/**
 * Whether a full response row belongs to a hidden school.
 * @param {{ schoolName?: string }} response
 * @param {Set<string>} hiddenKeys
 * @param {Map} lookup from buildAliasLookup
 */
export function isHiddenSchoolResponse(response, hiddenKeys, lookup) {
  if (!hiddenKeys.size) return false;
  const key = lookup
    ? resolveSchoolKey(response.schoolName, lookup)
    : normalizeSchool(response.schoolName) || "(unknown)";
  return hiddenKeys.has(key);
}

// ── Similarity / suggestions ────────────────────────────────────────────────

function stripNoise(s) {
  return s
    .replace(/\b(college|school|vidyalaya|maha|national|central)\b/gi, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[m][n];
}

function similarity(a, b) {
  const na = stripNoise(normalizeSchool(a));
  const nb = stripNoise(normalizeSchool(b));
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  if (na.includes(nb) || nb.includes(na)) return 0.92;
  const dist = levenshtein(na, nb);
  const maxLen = Math.max(na.length, nb.length);
  return 1 - dist / maxLen;
}

/**
 * Collect distinct raw school labels from records.
 * @returns {{ key: string, label: string, count: number }[]}
 */
export function distinctSchoolEntries(records) {
  const map = new Map();
  for (const r of records) {
    const key = normalizeSchool(r.schoolName);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, { key, label: (r.schoolName ?? "").trim(), count: 0 });
    }
    map.get(key).count += 1;
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

/**
 * Suggest groups of schools that may be duplicates.
 * @param {object[]} records
 * @param {AliasGroup[]} existingGroups
 * @param {number} threshold 0–1
 */
export function suggestSimilarSchools(records, existingGroups = [], threshold = 0.82) {
  const mergedKeys = new Set();
  for (const g of existingGroups) {
    mergedKeys.add(normalizeSchool(g.canonicalName));
    for (const k of g.memberKeys || []) mergedKeys.add(normalizeSchool(k));
  }

  const entries = distinctSchoolEntries(records).filter(
    (e) => !mergedKeys.has(e.key)
  );

  const suggestions = [];
  const used = new Set();

  for (let i = 0; i < entries.length; i++) {
    if (used.has(entries[i].key)) continue;
    const cluster = [entries[i]];
    used.add(entries[i].key);

    for (let j = i + 1; j < entries.length; j++) {
      if (used.has(entries[j].key)) continue;
      const sim = similarity(entries[i].label, entries[j].label);
      if (sim >= threshold) {
        cluster.push(entries[j]);
        used.add(entries[j].key);
      }
    }

    if (cluster.length > 1) {
      suggestions.push({
        id: `suggest-${cluster.map((c) => c.key).join("-")}`,
        schools: cluster,
        suggestedName: cluster.sort((a, b) => b.count - a.count)[0].label,
      });
    }
  }

  return suggestions;
}

export function createGroupFromSelection(canonicalName, selectedEntries) {
  const memberKeys = selectedEntries.map((e) => e.key);
  const memberLabels = selectedEntries.map((e) => e.label);
  return {
    id: `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    canonicalName: canonicalName.trim(),
    memberKeys,
    memberLabels,
  };
}

export function mergeGroups(existing, newGroup) {
  const keys = new Set(
    [newGroup.canonicalName, ...(newGroup.memberKeys || [])].map(normalizeSchool)
  );
  const labels = new Set([
    newGroup.canonicalName,
    ...(newGroup.memberLabels || []),
  ]);

  const kept = [];
  for (const g of existing) {
    const gKeys = [
      normalizeSchool(g.canonicalName),
      ...(g.memberKeys || []).map(normalizeSchool),
    ];
    if (gKeys.some((k) => keys.has(k))) {
      gKeys.forEach((k) => keys.add(k));
      (g.memberLabels || []).forEach((l) => labels.add(l));
    } else {
      kept.push(g);
    }
  }

  const canonicalKey = normalizeSchool(newGroup.canonicalName);
  const memberKeys = [...keys].filter((k) => k && k !== canonicalKey);

  return [
    ...kept,
    {
      ...newGroup,
      memberKeys,
      memberLabels: [...labels],
    },
  ];
}
